import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHonorTier } from "@/lib/honor";
import { RetryBox } from "@/components/RetryBox";
import { EmojiButton } from "@/components/EmojiButton";
import { ReportButton } from "@/components/ReportButton";
import { ReactionButton } from "@/components/ReactionButton";
import { Loader2, Send, Sparkles, Trash2, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { streamWrite } from "@/components/AiWriter";
import { TranslateButton } from "@/components/ai/TranslateButton";
import { toast } from "sonner";
import { successPop } from "@/lib/celebrate";
import { api, formatErr } from "@/lib/api";
import { UserAvatar, UserLink } from "@/components/UserBits";
import { TopCrown } from "@/components/TopCrown";
import { RoleBadge } from "@/components/RoleBadge";
import { timeAgo } from "@/lib/timeAgo";
import { Glitter } from "@/components/SupporterGlitter";
import { spooky } from "@/components/halloween/spooky";

const Comment = ({ c, small, mention, onDelete, onLike, onReply }) => {
  const [tr, setTr] = useState(null);
  const honorTier = useHonorTier(c.user_id);
  return (
  <li className="flex gap-2" data-testid={`post-comment-${c.id}`}>
    <UserLink id={c.user_id}><UserAvatar userId={c.user_id} name={c.name} picture={c.picture} size={small ? "w-6 h-6" : "w-8 h-8"} text="text-[10px]" /></UserLink>
    <div className="min-w-0 flex-1">
      <div className={`rounded-2xl px-3 py-2 ${honorTier ? `tier-${honorTier} honor-bubble` : c.is_supporter ? "vip-bubble sp-glow sp-glow-sm" : "bg-rose-50/70"}`} data-vip={(c.is_supporter && !honorTier) || undefined} data-honor={honorTier || undefined}>
        {c.is_supporter && !honorTier && <Glitter size="sm" />}
        <p className="text-xs font-semibold flex items-center gap-1 flex-wrap"><UserLink id={c.user_id}>{c.name}</UserLink> {!honorTier && <RoleBadge creator={c.is_creator} moderator={c.is_moderator} supporter={c.is_supporter} chip />} {!honorTier && <TopCrown userId={c.user_id} />}</p>
        <p className="text-sm whitespace-pre-wrap break-words">{mention && <UserLink id={c.reply_to.user_id} className="font-semibold text-rose-600 mr-1">@{c.reply_to.name}</UserLink>}{spooky(tr || c.text, c.id)}</p>
      </div>
      <div className="flex items-center gap-3 px-2 mt-0.5 text-[11px] text-slate-400 whitespace-nowrap flex-wrap">
        <span>{timeAgo(c.created_at)}</span>
        <ReactionButton p={c} kind="comment" small onReact={(e) => onLike(c, e)} className="inline-flex items-center gap-1 font-semibold transition-colors !bg-transparent" />
        <button onClick={() => onReply(c)} data-testid={`post-comment-reply-${c.id}`} className="font-semibold hover:text-rose-500">Antworten</button>
        <ReportButton kind="comment" targetId={c.id} ownerId={c.user_id} />
        <TranslateButton text={c.text} testId={`comment-translate-${c.id}`} onText={setTr} />
        {c.can_delete && <button onClick={() => onDelete(c)} data-testid={`post-comment-delete-${c.id}`} className="hover:text-red-600"><Trash2 className="w-3 h-3" /></button>}
      </div>
    </div>
  </li>
  );
};

function ReplyForm({ target, onSend, onCancel }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [ai, setAi] = useState(false);
  const { user } = useAuth();
  const ref = useRef(null);
  useEffect(() => { ref.current?.focus(); }, [target.id]);
  const suggest = async () => {
    setAi(true);
    try { await streamWrite({ comment_id: target.id }, (t) => setText(t.replace(/\*\*/g, "").trim().slice(0, 500)), "/admin/ai/reply"); ref.current?.focus(); }
    catch (e) { toast.error(e.message); }
    finally { setAi(false); }
  };
  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setBusy(true);
    if (await onSend(text, target)) setText("");
    setBusy(false);
  };
  return (
    <form onSubmit={submit} className="flex gap-2 items-center" data-testid={`reply-form-${target.id}`}>
      <div className="flex-1 min-w-0 flex items-center h-9 pl-3 pr-1 rounded-full border border-pink-200 bg-white focus-within:ring-2 focus-within:ring-rose-300">
        <span className="text-xs font-semibold text-rose-600 shrink-0 mr-1 max-w-[40%] truncate">@{target.name}</span>
        <input ref={ref} value={text} onChange={(e) => setText(e.target.value)} maxLength={500} placeholder="Antworten…" data-testid={`reply-input-${target.id}`} className="flex-1 min-w-0 bg-transparent text-base sm:text-sm focus:outline-none" />
        <EmojiButton inputRef={ref} value={text} onChange={setText} testId={`reply-emoji-${target.id}`} className="w-7 h-7" />
        {user?.role === "admin" && <button type="button" onClick={suggest} disabled={ai} title="Antwort von ChatGPT vorschlagen lassen" data-testid={`reply-ai-${target.id}`} className="p-1 text-emerald-600 hover:text-emerald-700 disabled:opacity-60" aria-label="Antwort vorschlagen">{ai ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}</button>}
        <button type="button" onClick={onCancel} data-testid={`reply-cancel-${target.id}`} className="p-1 text-slate-400 hover:text-slate-600" aria-label="Abbrechen"><X className="w-3.5 h-3.5" /></button>
      </div>
      <button type="submit" disabled={busy || !text.trim()} data-testid={`reply-submit-${target.id}`} className="w-9 h-9 shrink-0 rounded-full pink-gradient text-white flex items-center justify-center disabled:opacity-50">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
      </button>
    </form>
  );
}

function Thread({ c, replies, replyTo, setReplyTo, onSend, ...h }) {
  const [open, setOpen] = useState(false);
  const shown = open || replies.length <= 2 ? replies : replies.slice(-2);
  return (
    <>
      <Comment c={c} onReply={(x) => setReplyTo({ id: c.id, parentId: x.id, name: x.name })} {...h} />
      {(replies.length > 0 || replyTo?.id === c.id) && (
        <li className="ml-10 space-y-2" data-testid={`post-comment-replies-${c.id}`}>
          {!open && replies.length > 2 && <button onClick={() => setOpen(true)} data-testid={`post-comment-more-${c.id}`} className="text-xs font-bold text-slate-500 hover:text-rose-600">{replies.length - 2} weitere Antworten anzeigen</button>}
          <ul className="space-y-2">
            {shown.map((r) => <Comment key={r.id} c={r} small mention={r.reply_to && r.reply_to.user_id !== c.user_id} onReply={(x) => setReplyTo({ id: c.id, parentId: x.id, name: x.name })} {...h} />)}
          </ul>
          {replyTo?.id === c.id && <ReplyForm target={{ id: replyTo.parentId, name: replyTo.name }} onSend={onSend} onCancel={() => setReplyTo(null)} />}
        </li>
      )}
    </>
  );
}

export function PostComments({ postId, onCount }) {
  const [list, setList] = useState(null);
  const [text, setText] = useState("");
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const load = useCallback(() => { setError(false); api.get(`/feed/posts/${postId}/comments`).then(({ data }) => setList(data)).catch(() => setError(true)); }, [postId]);
  useEffect(() => { load(); }, [load]);
  const { tops, byParent } = useMemo(() => {
    const byParent = {};
    (list || []).forEach((c) => { if (c.parent_id) (byParent[c.parent_id] ||= []).push(c); });
    return { tops: (list || []).filter((c) => !c.parent_id), byParent };
  }, [list]);

  const post = async (body) => {
    const { data } = await api.post(`/feed/posts/${postId}/comments`, body);
    successPop(document.activeElement);
    setList((l) => [...l, data]); onCount(1); window.dispatchEvent(new Event("points-changed"));
  };
  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setBusy(true);
    try { await post({ text }); setText(""); } catch (err) { toast.error(formatErr(err)); } finally { setBusy(false); }
  };
  const sendReply = async (t, target) => {
    try { await post({ text: t, parent_id: target.id }); return true; } catch (err) { toast.error(formatErr(err)); return false; }
  };
  const like = async (c, emoji) => {
    try { const { data } = await api.post(`/feed/comments/${c.id}/like`, emoji ? { emoji } : {}); setList((l) => l.map((x) => (x.id === c.id ? { ...x, ...data } : x))); }
    catch (err) { toast.error(formatErr(err)); }
  };
  const del = async (c) => {
    try {
      const { data } = await api.delete(`/feed/comments/${c.id}`);
      setList((l) => l.filter((x) => x.id !== c.id && x.parent_id !== c.id)); onCount(-(data.deleted || 1));
    } catch (err) { toast.error(formatErr(err)); }
  };

  return (
    <div className="space-y-3 pt-3 border-t border-pink-50" data-testid={`post-comments-${postId}`}>
      {error ? <RetryBox onRetry={load} text="Kommentare konnten nicht geladen werden." /> : !list ? <Loader2 className="w-4 h-4 animate-spin text-rose-500 mx-auto" /> : (
        <ul className="space-y-2.5">
          {tops.map((c) => <Thread key={c.id} c={c} replies={byParent[c.id] || []} replyTo={replyTo} setReplyTo={setReplyTo} onSend={sendReply} onDelete={del} onLike={like} />)}
        </ul>
      )}
      <form onSubmit={submit} className="flex gap-2">
        <div className="flex-1 min-w-0 flex items-center h-10 pl-4 pr-1 rounded-full border border-pink-200 bg-white focus-within:ring-2 focus-within:ring-rose-300">
          <input ref={inputRef} value={text} onChange={(e) => setText(e.target.value)} maxLength={500} placeholder="Kommentieren…" data-testid={`post-comment-input-${postId}`}
            className="flex-1 min-w-0 bg-transparent text-base sm:text-sm focus:outline-none" />
          <EmojiButton inputRef={inputRef} value={text} onChange={setText} testId={`post-comment-emoji-${postId}`} className="w-8 h-8" />
        </div>
        <button type="submit" disabled={busy || !text.trim()} data-testid={`post-comment-submit-${postId}`} className="w-10 h-10 shrink-0 rounded-full pink-gradient text-white flex items-center justify-center disabled:opacity-50">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </button>
      </form>
    </div>
  );
}
